
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";

import { QueryClientProvider } from "@tanstack/react-query";
import { createOptimizedQueryClient, preloadCriticalResources } from "@/utils/advancedCaching";
import { serviceWorkerManager } from "@/utils/serviceWorkerManager";
import { preloadRouteComponents } from "@/utils/codeSplitting";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { addResourceHints } from "@/utils/resourceHints";
import { reportWebVitals } from "@/utils/webVitalsReporting";
import { CommandPalette } from "@/components/CommandPalette";
import { RouteProgressIndicator, SmartScrollRestoration } from "@/components/navigation";
import { usePageLoader } from "@/hooks/usePageLoader";
import { PageLoader } from "@/components/PageLoader";
import { ConsentProvider } from "@/components/cookie-consent/ConsentProvider";
import { CookieConsentTab } from "@/components/cookie-consent/CookieConsentTab";
import { UserOnboardingProvider } from "@/contexts/UserOnboardingContext";
import { ErrorBoundary } from "@/components/ui/error-boundary";
import { EnhancedErrorBoundary } from "@/components/EnhancedErrorBoundary";
import { ErrorMonitor } from "@/components/ErrorMonitor";
import { useState, useEffect, Suspense, ReactNode } from "react";
import { ResourceLoader } from "@/components/ResourceLoader";
import { ConnectionStatus } from "@/components/ConnectionStatus";
import { DemoModeProvider } from "@/contexts/DemoModeContext";
import { LocaleProvider } from "@/contexts/LocaleContext";
import { TooltipProvider } from "@/components/ui/tooltip";
import { EmergencyModeIndicator } from "@/components/EmergencyModeIndicator";
import { MobilePerformanceOptimizer } from "@/components/mobile/MobilePerformanceOptimizer";
import { LazyCopilot } from "@/components/copilot/LazyCopilot";
import { PortalShell } from "@/components/portal/PortalShell";
import { RouteAccessGuard } from "@/components/auth/RouteAccessGuard";
import { GlobalStructuredData, PageStructuredData } from "@/components/seo/GlobalStructuredData";
import { AutoPageSEO } from "@/components/seo/PageSEO";
import { useIntentPrefetch } from "@/hooks/useIntentPrefetch";
import { RouteThemeProvider } from "@/components/preferences/RouteThemeProvider";
import { UserPreferenceSync } from "@/components/preferences/UserPreferenceSync";
// Removed useGlobalDiscountDetection - now handled in unified useDiscountCode hook
// Auth 2.0 system - no AuthProvider needed anymore
// import { AuthProvider } from "@/contexts/AuthContext";

// Safe TooltipProvider wrapper with error recovery
const SafeTooltipProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [tooltipError, setTooltipError] = useState(false);
  const [retryCount, setRetryCount] = useState(0);

  useEffect(() => {
    // Reset error state on mount and retry
    if (tooltipError && retryCount < 3) {
      const timer = setTimeout(() => {
        setTooltipError(false);
        setRetryCount(prev => prev + 1);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [tooltipError, retryCount]);

  if (tooltipError && retryCount >= 3) {
    // Fallback: render children without tooltip provider
    console.warn('TooltipProvider failed, rendering without tooltips');
    return <>{children}</>;
  }

  try {
    return (
      <ErrorBoundary
        fallback={
          <div onClick={() => setTooltipError(true)}>
            {children}
          </div>
        }
      >
        <TooltipProvider>
          {children}
        </TooltipProvider>
      </ErrorBoundary>
    );
  } catch (error) {
    console.error('TooltipProvider error:', error);
    setTooltipError(true);
    return <>{children}</>;
  }
};

// Core pages - always loaded
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";
import OAuthConsent from "./pages/OAuthConsent";
import Connect from "./pages/Connect";

// Lazy loaded pages with code splitting
import { createLazyComponent } from "@/utils/codeSplitting";

const About = createLazyComponent(() => import("./pages/About"), {
  fallback: <div className="min-h-screen flex items-center justify-center">Loading About...</div>
});

const Homepage2 = createLazyComponent(() => import("./pages/Homepage2"));

const Products = createLazyComponent(() => import("./pages/Products"));
const Portfolio = createLazyComponent(() => import("./pages/Portfolio"));
const Contact = createLazyComponent(() => import("./pages/Contact"));
const Privacy = createLazyComponent(() => import("./pages/Privacy"));
const Legal = createLazyComponent(() => import("./pages/Legal"));
const CookiePolicy = createLazyComponent(() => import("./pages/CookiePolicy"));
const GdprCompliance = createLazyComponent(() => import("./pages/GdprCompliance"));
const PriceBeatGuarantee = createLazyComponent(() => import("./pages/PriceBeatGuarantee"));
const PricingPage = createLazyComponent(() => import("./pages/PricingPage"));
const FAQ = createLazyComponent(() => import("./pages/FAQ"));
const Blog = createLazyComponent(() => import("./pages/Blog"));
const BlogPost = createLazyComponent(() => import("./pages/BlogPost"));
const BlogPostEdit = createLazyComponent(() => import("./pages/BlogPostEdit"));
const Marketing = createLazyComponent(() => import("./pages/Marketing"));
const MarketingDemo = createLazyComponent(() => import("./pages/MarketingDemo"));
const Admin = createLazyComponent(() => import("./pages/Admin"));
const Profile = createLazyComponent(() => import("./pages/Profile"));
const ClientLogin = createLazyComponent(() => import("./pages/ClientLogin"));
const ClientHubPage = createLazyComponent(() => import("./pages/ClientHubPage"));
const AuthCallback = createLazyComponent(() => import("./pages/auth/AuthCallback"));
const PasswordReset = createLazyComponent(() => import("./pages/PasswordReset"));
const ForgotPassword = createLazyComponent(() => import("./pages/ForgotPassword"));
const Auth = createLazyComponent(() => import("./pages/Auth"));
const Analytics = createLazyComponent(() => import("./pages/Analytics"));
const ICarlyAnalytics = createLazyComponent(() => import("./pages/ICarlyAnalytics"));
const Deployment = createLazyComponent(() => import("./pages/Deployment"));
const WebsiteWizard = createLazyComponent(() => import("./pages/WebsiteWizard"));
const PRDOutput = createLazyComponent(() => import("./pages/PRDOutput"));
const PaymentSuccess = createLazyComponent(() => import("./pages/PaymentSuccess"));
const Careers = createLazyComponent(() => import("./pages/Careers"));
const AIDiscovery = createLazyComponent(() => import("./pages/AIDiscovery"));
const AIAgents = createLazyComponent(() => import("./pages/AIAgents"));
const SecurityDashboard = createLazyComponent(() => import("./pages/SecurityDashboard"));
const UserAdoption = createLazyComponent(() => import("./pages/UserAdoption"));
const CheckMySite = createLazyComponent(() => import("./pages/CheckMySite"));
const CheckMySiteShare = createLazyComponent(() => import("./pages/CheckMySiteShare"));
const Sitemap = createLazyComponent(() => import("./pages/Sitemap"));
const ClientWorkspace = createLazyComponent(() => import("./pages/ClientWorkspace"));
const MerchantFeed = createLazyComponent(() => import("./pages/api/MerchantFeed"));
const MerchantFeedTSV = createLazyComponent(() => import("./pages/api/MerchantFeedTSV"));
const MerchantFeedCSV = createLazyComponent(() => import("./pages/api/MerchantFeedCSV"));
const SolutionsCompare = createLazyComponent(() => import("./pages/SolutionsCompare"));
const EmailBannersGallery = createLazyComponent(() => import("./pages/EmailBannersGallery"));
const Welcome = createLazyComponent(() => import("./pages/Welcome"));

// Dev-only and Admin pages
const ResponsiveQA = createLazyComponent(() => import("./pages/ResponsiveQA"));
const ReleaseReadiness = createLazyComponent(() => import("./pages/admin/ReleaseReadiness"));
const IncidentManagement = createLazyComponent(() => import("./pages/admin/IncidentManagement"));
const EdgeFunctionHealth = createLazyComponent(() => import("./pages/admin/EdgeFunctionHealth"));
const EmailPreview = createLazyComponent(() => import("./pages/admin/EmailPreview"));
// Demo Access Components
const DemoAccessGate = createLazyComponent(() => import("./components/demo/DemoAccessGate"));

// Onboarding Pages
const Onboarding = createLazyComponent(() => import("./pages/Onboarding"));
const PortalDashboard = createLazyComponent(() => import("./pages/portal/PortalDashboard"));
const PortalChecklist = createLazyComponent(() => import("./pages/portal/PortalChecklist"));
const PortalTraining = createLazyComponent(() => import("./pages/portal/PortalTraining"));
const PortalPolicies = createLazyComponent(() => import("./pages/portal/PortalPolicies"));
const PortalContractsNDA = createLazyComponent(() => import("./pages/portal/PortalContractsNDA"));
const PortalGoogleWorkspace = createLazyComponent(() => import("./pages/portal/PortalGoogleWorkspace"));
const PortalEmailSetup = createLazyComponent(() => import("./pages/portal/PortalEmailSetup"));
const PortalDocuments = createLazyComponent(() => import("./pages/portal/PortalDocuments"));
const PortalSupport = createLazyComponent(() => import("./pages/portal/PortalSupport"));
const PortalWelcomeSessions = createLazyComponent(() => import("./pages/portal/PortalWelcomeSessions"));
const PortalConnectedTools = createLazyComponent(() => import("./pages/portal/PortalConnectedTools"));
const PortalEmbeddedApp = createLazyComponent(() => import("./pages/portal/PortalEmbeddedApp"));
const PortalMyDay = createLazyComponent(() => import("./pages/portal/PortalMyDay"));
const PortalLearning = createLazyComponent(() => import("./pages/portal/PortalLearning"));
const PortalQuality = createLazyComponent(() => import("./pages/portal/PortalQuality"));

// Admin Onboarding Pages
const AdminOnboardingTemplates = createLazyComponent(() => import("./pages/admin/onboarding/AdminOnboardingTemplates"));
const AdminOnboardingUsers = createLazyComponent(() => import("./pages/admin/onboarding/AdminOnboardingUsers"));
const AdminOnboardingContent = createLazyComponent(() => import("./pages/admin/onboarding/AdminOnboardingContent"));
const AdminOnboardingPolicies = createLazyComponent(() => import("./pages/admin/onboarding/AdminOnboardingPolicies"));
const AdminOnboardingTraining = createLazyComponent(() => import("./pages/admin/onboarding/AdminOnboardingTraining"));
const AdminOnboardingIntegrations = createLazyComponent(() => import("./pages/admin/onboarding/AdminOnboardingIntegrations"));
const AdminOnboardingAnalytics = createLazyComponent(() => import("./pages/admin/onboarding/AdminOnboardingAnalytics"));
const AdminOnboardingInvitations = createLazyComponent(() => import("./pages/admin/onboarding/AdminOnboardingInvitations"));
const AdminOnboardingContracts = createLazyComponent(() => import("./pages/admin/onboarding/AdminOnboardingContracts"));
const AdminKnowledgeBase = createLazyComponent(() => import("./pages/admin/AdminKnowledgeBase"));
// Product Pages - lazy loaded
const WebsiteProductPage = createLazyComponent(() => import("./pages/products/WebsiteProductPage"));
const WebAppProductPage = createLazyComponent(() => import("./pages/products/WebAppProductPage"));
const CategoryPage = createLazyComponent(() => import("./pages/products/CategoryPage"));
const MarketingSalesSuitePage = createLazyComponent(() => import("./pages/products/MarketingSalesSuitePage"));
const FacilitiesManagementSuitePage = createLazyComponent(() => import("./pages/products/FacilitiesManagementSuitePage"));
const DigitalNewsroomPlatformPage = createLazyComponent(() => import("./pages/products/DigitalNewsroomPlatformPage"));
const DigitalNewsroomPlatformDemo = createLazyComponent(() => import("./pages/products/DigitalNewsroomPlatformDemo"));
const BpoQualityAssuranceSystemPage = createLazyComponent(() => import("./pages/products/BpoQualityAssuranceSystemPage"));
const QualityAssuranceSystemDemo = createLazyComponent(() => import("./pages/products/BpoQualityAssuranceSystemDemo"));
const CallRecordingSystemPage = createLazyComponent(() => import("./pages/products/CallRecordingSystemPage"));
const CallRecordingSystemDemo = createLazyComponent(() => import("./pages/products/CallRecordingSystemDemo"));
const WorkforceManagementSystemPage = createLazyComponent(() => import("./pages/products/WorkforceManagementSystemPage"));
const WorkforceManagementSystemDemo = createLazyComponent(() => import("./pages/products/WorkforceManagementSystemDemo"));
const ItServiceDeskPlatformPage = createLazyComponent(() => import("./pages/products/ItServiceDeskPlatformPage"));
const ItServiceDeskPlatformDemo = createLazyComponent(() => import("./pages/products/ItServiceDeskPlatformDemo"));
const FacilitiesManagementSuiteDemo = createLazyComponent(() => import("./pages/products/FacilitiesManagementSuiteDemo"));
const CoworkingManagementSuitePage = createLazyComponent(() => import("./pages/products/CoworkingManagementSuitePage"));
const CompletePayrollSystemPage = createLazyComponent(() => import("./pages/products/CompletePayrollSystemPage"));
const PayrollDemo = createLazyComponent(() => import("./pages/PayrollDemo"));
const EorDemo = createLazyComponent(() => import("./pages/EorDemo"));
const CoworkingDemo = createLazyComponent(() => import("./pages/CoworkingDemo"));
const GlobalEmployerOfRecordPage = createLazyComponent(() => import("./pages/products/GlobalEmployerOfRecordPage"));
const CenturionGlobalProtectPage = createLazyComponent(() => import("./pages/products/CenturionGlobalProtectPage"));
const CenturionGlobalProtectDemo = createLazyComponent(() => import("./pages/products/CenturionGlobalProtectDemo"));
const CompleteHrManagementPlatformPage = createLazyComponent(() => import("./pages/products/CompleteHrManagementPlatformPage"));
const CompleteHrManagementPlatformDemo = createLazyComponent(() => import("./pages/products/CompleteHrManagementPlatformDemo"));
const PassengerTransportationManagementPage = createLazyComponent(() => import("./pages/products/PassengerTransportationManagementPage"));
const PassengerTransportationManagementDemo = createLazyComponent(() => import("./pages/products/PassengerTransportationManagementDemo"));
const TimeAttendanceSystemPage = createLazyComponent(() => import("./pages/products/TimeAttendanceSystemPage"));
const TimeAttendanceSystemDemo = createLazyComponent(() => import("./pages/products/TimeAttendanceSystemDemo"));
const ProductPageTemplate = createLazyComponent(() => import("./components/products/ProductPageTemplate").then(m => ({ default: m.ProductPageTemplate })));
const WebsiteWebAppDemo = createLazyComponent(() => import("./pages/WebsiteWebAppDemo"));
const A11yCheck = createLazyComponent(() => import("./pages/A11yCheck"));

const queryClient = createOptimizedQueryClient();

// AppContent component that contains Router-dependent logic
function AppContent() {
  const { isLoading } = usePageLoader();
  const [showCommandPalette, setShowCommandPalette] = useState(false);
  
  // Intent-based prefetching for performance optimization
  useIntentPrefetch();
  
  // Removed global discount detection - now handled in unified useDiscountCode hook
  

  // Add keyboard shortcut for command palette (Cmd+K / Ctrl+K)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setShowCommandPalette(true);
      }
    };
    const handleOpenCommandPalette = () => setShowCommandPalette(true);

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('dialgia:open-command-palette', handleOpenCommandPalette);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('dialgia:open-command-palette', handleOpenCommandPalette);
    };
  }, []);

  if (isLoading) {
    return <PageLoader isLoading={isLoading} />;
  }

  return (
    <EnhancedErrorBoundary context="App">
      {/* Skip to main content link for keyboard users */}
      <a href="#main-content" className="skip-link">
        Skip to main content
      </a>
      <RouteProgressIndicator />
      <SmartScrollRestoration />
      <CommandPalette 
        isOpen={showCommandPalette}
        onClose={() => setShowCommandPalette(false)}
      />
      <ConnectionStatus />
      <EmergencyModeIndicator />
      <MobilePerformanceOptimizer />
      <CookieConsentTab />
      <LazyCopilot />
      <GlobalStructuredData />
      <PageStructuredData />
      <AutoPageSEO />
      <RouteAccessGuard>
        <Routes>
        <Route path="/" element={<Index />} />
        <Route path="/homepage-2" element={<Homepage2 />} />
        <Route path="/about" element={<About />} />
        <Route path="/welcome" element={<Welcome />} />
        
          <Route path="/products" element={<Products />} />
          <Route path="/products/category/:categorySlug" element={<CategoryPage />} />
          <Route path="/products/marketing-sales-suite" element={<MarketingSalesSuitePage />} />
          <Route path="/products/facilities-management-suite" element={<FacilitiesManagementSuitePage />} />
          <Route path="/products/digital-newsroom-platform" element={<DigitalNewsroomPlatformPage />} />
          <Route path="/products/quality-assurance-system" element={<BpoQualityAssuranceSystemPage />} />
          <Route path="/products/call-recording-system" element={<CallRecordingSystemPage />} />
          <Route path="/products/workforce-management-system" element={<WorkforceManagementSystemPage />} />
          <Route path="/workforce-management/demo" element={<WorkforceManagementSystemDemo />} />
          <Route path="/products/it-service-desk-platform" element={<ItServiceDeskPlatformPage />} />
          <Route path="/service-desk/demo" element={<ItServiceDeskPlatformDemo />} />
          <Route path="/products/coworking-management-suite" element={<CoworkingManagementSuitePage />} />
          <Route path="/products/website-webapp-suite" element={<WebsiteProductPage />} />
          <Route path="/products/complete-payroll-system" element={<CompletePayrollSystemPage />} />
          <Route path="/products/global-employer-of-record" element={<GlobalEmployerOfRecordPage />} />
          <Route path="/products/centurion-global-protect" element={<CenturionGlobalProtectPage />} />
          <Route path="/products/complete-hr-management-platform" element={<CompleteHrManagementPlatformPage />} />
          <Route path="/products/passenger-transportation-management" element={<PassengerTransportationManagementPage />} />
          <Route path="/products/time-attendance-system" element={<TimeAttendanceSystemPage />} />
          
          {/* Demo Access Gates */}
          <Route path="/demo-access/:productType" element={<DemoAccessGate />} />
          
          {/* Protected Demo Routes */}
          <Route path="/facilities-management/demo" element={<FacilitiesManagementSuiteDemo />} />
          <Route path="/digital-newsroom/demo" element={<DigitalNewsroomPlatformDemo />} />
          <Route path="/quality-assurance/demo" element={<QualityAssuranceSystemDemo />} />
          <Route path="/call-recording/demo" element={<CallRecordingSystemDemo />} />
          <Route path="/payroll/demo" element={<PayrollDemo />} />
          <Route path="/eor/demo" element={<EorDemo />} />
          <Route path="/centurion-global-protect/demo" element={<CenturionGlobalProtectDemo />} />
          <Route path="/hr-management/demo" element={<CompleteHrManagementPlatformDemo />} />
          <Route path="/passenger-transportation/demo" element={<PassengerTransportationManagementDemo />} />
          <Route path="/time-attendance/demo" element={<TimeAttendanceSystemDemo />} />
          <Route path="/products/coworking-management-suite/demo" element={<CoworkingDemo />} />
          <Route path="/products/website-webapp-suite/demo" element={<WebsiteWebAppDemo />} />
         <Route path="/api/merchant-feed.xml" element={<MerchantFeed />} />
         <Route path="/api/merchant-feed.tsv" element={<MerchantFeedTSV />} />
         <Route path="/api/merchant-feed.csv" element={<MerchantFeedCSV />} />
        <Route path="/products/:productId" element={<ProductPageTemplate />} />
        <Route path="/portfolio" element={<Portfolio />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/legal" element={<Legal />} />
        <Route path="/cookie-policy" element={<CookiePolicy />} />
        <Route path="/gdpr-compliance" element={<GdprCompliance />} />
        <Route path="/price-beat-guarantee" element={<PriceBeatGuarantee />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/ai-discovery" element={<AIDiscovery />} />
        <Route path="/ai-agents" element={<AIAgents />} />
        <Route path="/solutions/compare" element={<SolutionsCompare />} />
        <Route path="/email-banners" element={<EmailBannersGallery />} />
        <Route path="/faq" element={<FAQ />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:slug" element={<BlogPost />} />
          <Route path="/blog/:slug/edit" element={<BlogPostEdit />} />
          <Route path="/marketing" element={<Marketing />} />
          <Route path="/marketing/demo" element={<MarketingDemo />} />
        <Route path="/admin" element={<Admin />} />
        <Route path="/profile" element={<Profile />} />
        <Route path="/my-profile" element={<Navigate to="/profile" replace />} />
        <Route path="/client-profile" element={<Navigate to="/profile" replace />} />
        <Route path="/security" element={<SecurityDashboard />} />
        <Route path="/user-adoption" element={<UserAdoption />} />
        <Route path="/checkmysite" element={<CheckMySite />} />
        <Route path="/checkmysite/share/:token" element={<CheckMySiteShare />} />
        <Route path="/sitemap" element={<Sitemap />} />
        <Route path="/client-login" element={<ClientLogin />} />
        <Route path="/client-portal" element={<Navigate to="/client-hub" replace />} />
        <Route path="/client-hub" element={<ClientHubPage />} />
        <Route path="/client-workspace" element={<Navigate to="/client-hub" replace />} />
        <Route path="/workspace/:projectId" element={<ClientWorkspace />} />
        <Route path="/auth" element={<Auth />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/password-reset" element={<PasswordReset />} />
        <Route path="/analytics" element={<Analytics />} />
        <Route path="/icarly-analytics" element={<ICarlyAnalytics />} />
        <Route path="/deployment" element={<Deployment />} />
        
        <Route path="/website-wizard" element={
          <EnhancedErrorBoundary 
            context="WebsiteWizard"
            fallback={
              <div className="min-h-screen flex items-center justify-center">
                <div className="text-center">
                  <div className="text-xl font-semibold mb-2">Website Wizard Unavailable</div>
                  <div className="text-muted-foreground">Please try refreshing the page</div>
                </div>
              </div>
            }
          >
            <WebsiteWizard />
          </EnhancedErrorBoundary>
        } />
        <Route path="/prd/:scopeId" element={<PRDOutput />} />
        <Route path="/careers" element={<Careers />} />
        <Route path="/payment-success" element={<PaymentSuccess />} />
        
        {/* Admin routes */}
        <Route path="/admin/release-readiness" element={<ReleaseReadiness />} />
        <Route path="/admin/incidents" element={<IncidentManagement />} />
        <Route path="/admin/edge-function-health" element={<EdgeFunctionHealth />} />
        <Route path="/admin/email-preview" element={<EmailPreview />} />
        
        {/* Onboarding routes */}
        <Route path="/onboarding" element={<Onboarding />} />
        
        {/* Employee Portal routes */}
        <Route path="/portal" element={<PortalShell />}>
          <Route index element={<PortalDashboard />} />
          <Route path="/portal/checklist" element={<PortalChecklist />} />
          <Route path="/portal/training" element={<PortalTraining />} />
          <Route path="/portal/policies" element={<PortalPolicies />} />
          <Route path="/portal/contracts-nda" element={<PortalContractsNDA />} />
          <Route path="/portal/google-workspace" element={<PortalGoogleWorkspace />} />
          <Route path="/portal/email-setup" element={<PortalEmailSetup />} />
          <Route path="/portal/documents" element={<PortalDocuments />} />
          <Route path="/portal/support" element={<PortalSupport />} />
          <Route path="/portal/welcome-sessions" element={<PortalWelcomeSessions />} />
          <Route path="/portal/connected-tools" element={<PortalConnectedTools />} />
          <Route path="/portal/my-day" element={<PortalMyDay />} />
          <Route path="/portal/learning" element={<PortalLearning />} />
          <Route path="/portal/quality" element={<PortalQuality />} />
          <Route path="/portal/apps/:integrationKey" element={<PortalEmbeddedApp />} />
        </Route>
        
        {/* Admin Onboarding routes */}
        <Route path="/admin/onboarding/templates" element={<AdminOnboardingTemplates />} />
        <Route path="/admin/onboarding/users" element={<AdminOnboardingUsers />} />
        <Route path="/admin/onboarding/content" element={<AdminOnboardingContent />} />
        <Route path="/admin/onboarding/policies" element={<AdminOnboardingPolicies />} />
        <Route path="/admin/onboarding/training" element={<AdminOnboardingTraining />} />
        <Route path="/admin/onboarding/integrations" element={<AdminOnboardingIntegrations />} />
        <Route path="/admin/onboarding/analytics" element={<AdminOnboardingAnalytics />} />
        <Route path="/admin/onboarding/invitations" element={<AdminOnboardingInvitations />} />
        <Route path="/admin/onboarding/contracts" element={<AdminOnboardingContracts />} />
        <Route path="/admin/knowledge-base" element={<AdminKnowledgeBase />} />
        
        {/* Dev-only routes - hidden in production */}
        {import.meta.env.DEV && (
          <>
            <Route path="/responsive-qa" element={<ResponsiveQA />} />
            <Route path="/a11y-check" element={<A11yCheck />} />
          </>
        )}
        <Route path="/.lovable/oauth/consent" element={<OAuthConsent />} />
        <Route path="/connect" element={<Connect />} />

        <Route path="*" element={<NotFound />} />
        </Routes>
      </RouteAccessGuard>
    </EnhancedErrorBoundary>
  );
}

function App() {
  // Initialize performance optimizations
  useEffect(() => {
    // Apply resource hints immediately to warm connections
    addResourceHints();
    
    // Initialize Web Vitals RUM collection for performance monitoring
    reportWebVitals();

    const schedule = () => {
      // Preload critical resources and routes off the critical path
      preloadCriticalResources();
      preloadRouteComponents();

      // Initialize service worker (centralized)
      serviceWorkerManager.initialize().catch(error => {
        console.warn('Service worker initialization failed:', error);
      });
    };

    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(schedule);
    } else {
      setTimeout(schedule, 150);
    }
  }, []);

  return (
    <ErrorMonitor>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <RouteThemeProvider>
          <UserPreferenceSync />
          <Suspense fallback={
            <div className="min-h-screen flex items-center justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          }>
            <LocaleProvider>
              <ConsentProvider>
                <ErrorBoundary>
                  <TooltipProvider>
                    <UserOnboardingProvider>
                      <Toaster />
                      <Sonner />
                        <DemoModeProvider>
                          <AppContent />
                        </DemoModeProvider>
                    </UserOnboardingProvider>
                  </TooltipProvider>
                </ErrorBoundary>
              </ConsentProvider>
            </LocaleProvider>
          </Suspense>
        </RouteThemeProvider>
        </BrowserRouter>
      </QueryClientProvider>
    </ErrorMonitor>
  );
}

export default App;
